import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { FieldDefinition, SectionConfig } from '../../types/schema';
import FieldEditor from './FieldEditor';

interface Props {
  sectionKey: string;
  section: SectionConfig;
  sections: Record<string, SectionConfig>;
  fields: Array<[string, FieldDefinition]>;
  onFieldChange: (fieldName: string, field: FieldDefinition) => void;
  globalActiveTab?: 'general' | 'scoring' | 'llm' | null;
  onTabChanged?: () => void;
  onAddField?: (sectionName: string) => void;
  onDeleteField?: (fieldName: string) => void;
}

const SectionAccordion: React.FC<Props> = ({
  sectionKey,
  section,
  sections,
  fields,
  onFieldChange,
  globalActiveTab,
  onTabChanged,
  onAddField,
  onDeleteField,
}) => {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [defaultActiveTab, setDefaultActiveTab] = useState<'general' | 'scoring' | 'llm'>('general');
  const [pendingDeleteField, setPendingDeleteField] = useState<string | null>(null);

  // Mettre à jour l'onglet par défaut quand globalActiveTab change
  useEffect(() => {
    if (globalActiveTab) {
      setDefaultActiveTab(globalActiveTab);
    }
  }, [globalActiveTab]);

  const expandAll = () => {
    setExpandedFields(new Set(fields.map(([name]) => name)));
  };

  const collapseAll = () => {
    setExpandedFields(new Set());
  };

  const toggleField = (fieldName: string) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(fieldName)) {
      newExpanded.delete(fieldName);
    } else {
      newExpanded.add(fieldName);
    }
    setExpandedFields(newExpanded);
  };

  const handleDeleteConfirm = (fieldName: string) => {
    if (onDeleteField) {
      onDeleteField(fieldName);
    }
    setPendingDeleteField(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <span>{section.label_fr}</span>
          <span className="text-sm text-gray-400 font-normal">({fields.length} champs)</span>
        </h2>

        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
          >
            ▼ Tout développer
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
          >
            ▶ Tout réduire
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {fields.map(([fieldName, field]) => (
          <div key={fieldName} className="border rounded-lg bg-white">
            <div className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50">
              <button
                type="button"
                onClick={() => toggleField(fieldName)}
                className="flex items-center gap-3 flex-1 text-left"
              >
                {expandedFields.has(fieldName) ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
                <span className="font-mono text-sm">{fieldName}</span>
                <span className="text-gray-500 text-sm">— {field.ui.label_fr}</span>
                {field.scoring.weight === 0 && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                    Exclu du scoring
                  </span>
                )}
              </button>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>{field.type}</span>
                {field.required && <span className="text-red-500">*</span>}
                {onDeleteField && (
                  <button
                    type="button"
                    onClick={() => setPendingDeleteField(pendingDeleteField === fieldName ? null : fieldName)}
                    className="ml-1 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    title="Supprimer ce champ"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>

            {pendingDeleteField === fieldName && (
              <div className="border-t bg-red-50 px-4 py-3 flex items-center gap-3 text-sm">
                <span className="text-red-700">Supprimer ce champ ?</span>
                <button
                  type="button"
                  onClick={() => handleDeleteConfirm(fieldName)}
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                >
                  Oui
                </button>
                <button
                  type="button"
                  onClick={() => setPendingDeleteField(null)}
                  className="px-3 py-1 border rounded hover:bg-gray-100 text-xs"
                >
                  Non
                </button>
              </div>
            )}

            {expandedFields.has(fieldName) && (
              <div className="border-t px-4 py-4">
                <FieldEditor
                  fieldName={fieldName}
                  field={field}
                  sections={sections}
                  onChange={(updated) => onFieldChange(fieldName, updated)}
                  globalActiveTab={globalActiveTab}
                  defaultActiveTab={defaultActiveTab}
                  onTabChanged={onTabChanged}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {onAddField && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => onAddField(sectionKey)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          >
            <Plus size={16} />
            Ajouter un champ
          </button>
        </div>
      )}
    </div>
  );
};

export default SectionAccordion;
